import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthState } from "../../context/AuthProvider";
import { API, Notify } from "../../utils";
import IMAGES from "../../assets";
import { MdPerson, MdEmail, MdLock, MdCloudUpload, MdArrowBack } from "react-icons/md";

const RegisterPage = () => {
  const [credentials, setCredentials] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    profilePic: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [imagePreview, setimagePreview] = useState(IMAGES.user);

  const navigate = useNavigate();
  const { setAuth } = AuthState();

  const handleCredentials = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const handleProfilePic = async (e) => {
    setIsLoading(true);
    const profilePic = e.target.files[0];

    if (!profilePic) {
      setIsLoading(false);
      return;
    }

    if (
      profilePic.type !== "image/jpeg" &&
      profilePic.type !== "image/jpg" &&
      profilePic.type !== "image/png"
    ) {
      e.target.value = null;
      setimagePreview(IMAGES.user);
      setIsLoading(false);
      return Notify("Only .jpeg, .jpg and .png supported", "warn");
    }

    if (profilePic.size > 2 * 1024 * 1024) {
      e.target.value = null;
      setimagePreview(IMAGES.user);
      setIsLoading(false);
      return Notify("Please upload image under 2 MB", "warn");
    }

    const formData = new FormData();
    formData.append("file", profilePic);
    formData.append("upload_preset", `${process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET}`);
    formData.append("cloud_name", `${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}`);

    try {
      const cloudinaryResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
        { method: "POST", body: formData }
      );
      const cloudinaryData = await cloudinaryResponse.json();

      setCredentials({
        ...credentials,
        profilePic: cloudinaryData.secure_url.toString(),
      });

      const reader = new FileReader();
      reader.readAsDataURL(profilePic);
      reader.onload = () => setimagePreview(reader.result);

      setIsLoading(false);
      return Notify("Profile picture uploaded", "success");
    } catch (error) {
      e.target.value = null;
      setimagePreview(IMAGES.user);
      setIsLoading(false);
      return Notify("Upload failed", "error");
    }
  };

  const registerHandler = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!credentials.name || !credentials.email || !credentials.password || !credentials.confirmPassword) {
      setIsLoading(false);
      return Notify("Please Fill all the Fields", "warn");
    }

    if (credentials.password !== credentials.confirmPassword) {
      setIsLoading(false);
      return Notify("Passwords Do Not Match", "warn");
    }

    if (credentials.password.length < 8) {
      setIsLoading(false);
      return Notify("Password must be at least 8 characters", "warn");
    }

    try {
      const response = await API.post("/api/auth/register", {
        name: credentials.name,
        email: credentials.email,
        password: credentials.password,
        profilePic: credentials.profilePic,
      });
      const data = response.data;

      if (data.success) {
        localStorage.setItem("auth", JSON.stringify(data));
        setAuth(data);
        setIsLoading(false);
        navigate("/dashboard");
        return Notify("Account created successfully", "success");
      } else {
        setIsLoading(false);
        return Notify(data.error, "error");
      }
    } catch (error) {
      setIsLoading(false);
      const errorMsg = error.response?.data?.error || "Internal server error";
      return Notify(errorMsg, "error");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-6">
      <div className="w-full max-w-[500px] bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-500 my-8">
        <div className="bg-blue-600 p-8 text-white relative">
          <Link to="/login" className="absolute top-6 left-6 text-blue-200 hover:text-white transition-colors">
            <MdArrowBack className="w-6 h-6" />
          </Link>
          <div className="text-center">
            <h1 className="text-2xl font-bold tracking-tight">Join Kaviraj Agencies</h1>
            <p className="text-blue-100 text-sm mt-1">Create your staff account</p>
          </div>
        </div>

        <form onSubmit={registerHandler} className="p-8 space-y-4">
          {/* Profile Pic Upload */}
          <div className="flex flex-col items-center mb-6">
            <div className="relative group cursor-pointer">
              <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-gray-100 shadow-md group-hover:border-blue-100 transition-all">
                <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
              </div>
              <label className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full cursor-pointer shadow-lg hover:bg-blue-700 transition-colors">
                <MdCloudUpload className="w-4 h-4" />
                <input type="file" className="hidden" accept="image/*" onChange={handleProfilePic} />
              </label>
            </div>
            <p className="text-[10px] text-gray-400 mt-2 font-bold uppercase tracking-widest">Profile Picture</p>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5 ml-1">Full Name</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 group-focus-within:text-blue-600 transition-colors">
                  <MdPerson className="w-5 h-5" />
                </div>
                <input
                  type="text" name="name" required
                  className="w-full pl-11 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:border-blue-500 focus:bg-white transition-all text-sm"
                  placeholder="John Doe"
                  value={credentials.name}
                  onChange={handleCredentials}
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5 ml-1">Email Address</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 group-focus-within:text-blue-600 transition-colors">
                  <MdEmail className="w-5 h-5" />
                </div>
                <input
                  type="email" name="email" required
                  className="w-full pl-11 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:border-blue-500 focus:bg-white transition-all text-sm"
                  placeholder="john@example.com"
                  value={credentials.email}
                  onChange={handleCredentials}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5 ml-1">Password</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 group-focus-within:text-blue-600 transition-colors">
                    <MdLock className="w-5 h-5" />
                  </div>
                  <input
                    type="password" name="password" required
                    className="w-full pl-11 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:border-blue-500 focus:bg-white transition-all text-sm"
                    placeholder="••••••••"
                    value={credentials.password}
                    onChange={handleCredentials}
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5 ml-1">Confirm</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 group-focus-within:text-blue-600 transition-colors">
                    <MdLock className="w-5 h-5" />
                  </div>
                  <input
                    type="password" name="confirmPassword" required
                    className="w-full pl-11 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:border-blue-500 focus:bg-white transition-all text-sm"
                    placeholder="••••••••"
                    value={credentials.confirmPassword}
                    onChange={handleCredentials}
                  />
                </div>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-blue-200 transition-all active:scale-95 disabled:opacity-70 disabled:active:scale-100 mt-4"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto"></div>
            ) : (
              "Create My Account"
            )}
          </button>

          <div className="text-center pt-2">
            <p className="text-gray-500 text-sm">
              Already have an account?&nbsp;
              <Link to="/login" className="text-blue-600 font-bold hover:underline">Log in</Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage;
